import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentService } from 'src/app/core/services/document.service';
import { CdkStepper } from '@angular/cdk/stepper';
import { ToastService } from 'src/app/core/services/toast.service';
import { TemplateService } from 'src/app/core/services/template.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ApiValidationError } from 'src/app/shared/models/api-validation.model';
import { normalizeApiValidationError } from 'src/app/shared/utils/api-validation.util';
import { ConfirmModalService } from 'src/app/core/services/confirm-modal.service';

const DOCUMENT_NAME_PATTERN = /^[A-Za-z0-9 ]+$/;
const RECIPIENT_NAME_PATTERN = /^(?!\s*$)[A-Za-z ]+$/;
@Component({
  selector: 'app-send-document',
  templateUrl: './send-document.component.html',
  styleUrl: './send-document.component.scss',
  standalone: false
})
export class SendDocumentComponent implements OnInit {

  breadCrumbItems: Array<{}> | undefined;
  documentForm!: FormGroup;
  recipientsList: any = [];
  selectedFile: File | undefined;
  pdfPreviewUrl!: string;
  updatedPdf: string | undefined;
  isShowDoc: boolean = false;
  signData: any = {};
  loading: boolean = false;
  apiValidationError: ApiValidationError | null = null;
  apiValidationTitle = 'Document request failed';
  submissionInProgress = false;

  @ViewChild('cdkStepper', { static: false })
  stepper!: CdkStepper;
  @ViewChild('apiValidationMessage')
  apiValidationMessage?: ElementRef<HTMLElement>;
  currentStepIndex = 0;
  templateId: string = ''
  fileError: string = '';

  constructor
    (
      private fb: FormBuilder,
      public documentService: DocumentService,
      private router: Router,
      private activatedRoute: ActivatedRoute,
      private toastService: ToastService,
      private templateService: TemplateService,
      private confirmModalService: ConfirmModalService
    ) { }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe((params: any) => {
      this.templateId = params?.templateId;
      if (this.templateId) {
        this.isUsedTemplateDoc = true;
        this.getTemplates();
      }
    });

    this.breadCrumbItems = [{ label: 'AVSS eSign' }, { label: 'Send Document', active: true }];
    this.createDocument();
    this.signers.valueChanges.subscribe(value => {
      this.recipientsList = value;
    });
  }

  ngAfterViewInit() {
    this.stepper.selectionChange.subscribe(event => {
      this.currentStepIndex = event.selectedIndex;
    });
  }

  get signers(): FormArray {
    return this.documentForm.get('signers') as FormArray;
  }

  addRecipient() {
    this.signers.push(this.createSigner());
  }

  removeRecipient(index: number) {
    if (this.signers.length > 1) {
      this.signers.removeAt(index);
    }
  }

  createDocument() {
    this.documentForm = this.fb.group({
      name: ['', [
        Validators.required,
        Validators.pattern(DOCUMENT_NAME_PATTERN)
      ]],
      livePhoto: [true],
      description: [''],
      signers: this.fb.array([this.createSigner()]),
      signingOrder: ['sequential'],
      message: ['']
    });
  }

  createSigner(): FormGroup {
    return this.fb.group({
      name: ['', [
        Validators.required,
        Validators.pattern(RECIPIENT_NAME_PATTERN)
      ]],
      email: ['', [
        Validators.required,
        Validators.email,
        this.noDuplicateDomainValidator,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-z0-9-]+\.(?!.*\.\1)([a-z]{2,})$/i)]
      ],
      esignType: ['1', Validators.required],
      phone: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)]]
    });
  }

  onSubmit() {
    if (this.loading || this.submissionInProgress) return;

    if (!this.validateBeforeSubmit(true)) {
      return;
    }

    const doc = {
      ...this.documentForm.value,
      file: this.selectedFile,
      signers: this.documentService.getSigners
    };

    this.openSendConfirmation(() => this.sendDocument(doc));
  }

  private sendDocument(doc: any): void {
    this.apiValidationTitle = 'Document request failed';
    this.clearApiValidationMessage();
    this.loading = true;
    this.documentService.createDocument(doc).subscribe({
      next: (res: any) => {
        this.submissionInProgress = false;
        this.router.navigate(['/customer/esign/my-document']);
        this.loading = false;
        this.apiValidationError = null;
        this.updatedPdf = '';
        this.selectedFile = undefined;
        this.documentService.setSigners = [];
        this.toastService.showToaster(
          "Success",
          "Document has been created successfully.",
          "success"
        );
      },
      error: (error) => {
        this.loading = false;
        this.submissionInProgress = false;

        this.apiValidationError =
          normalizeApiValidationError(error);
      }
    });
  }

  submitTemplateDoc() {
    if (this.loading || this.submissionInProgress) return;

    if (!this.validateBeforeSubmit(false)) {
      return;
    }

    const documentData = {
      signers: this.documentForm.value.signers,
      signingOrder: this.documentForm.value.signingOrder,
      livePhoto: this.documentForm.value.livePhoto,
      file: this.selectedFile,
    };

    this.openSendConfirmation(
      () => this.sendTemplateDocument(documentData)
    );
  }

  private sendTemplateDocument(documentData: any): void {
    this.apiValidationTitle = 'Document request failed';
    this.clearApiValidationMessage();
    this.loading = true;

    this.documentService
      .createDocument(documentData, this.templateId)
      .subscribe({
        next: (res: any) => {
          this.submissionInProgress = false;
          this.router.navigate([
            '/customer/esign/my-document'
          ]);

          this.loading = false;
          this.apiValidationError = null;

          this.selectedFile = undefined;
          this.documentService.setSigners = [];

          this.toastService.showToaster(
            "Success",
            res.message ||
            "Document has been created successfully.",
            "success"
          );
        },

        error: (error) => {
          this.loading = false;
          this.submissionInProgress = false;

          this.apiValidationError =
            normalizeApiValidationError(error);
        }
      });
  }

  private validateBeforeSubmit(requireSignatures: boolean): boolean {
    this.clearApiValidationMessage();
    this.normalizeNameValues();
    this.documentForm.markAllAsTouched();
    const validationMessages: string[] = [];

    const nameControl = this.documentForm.get('name');
    if (nameControl?.hasError('required')) {
      validationMessages.push('Document Name is required.');
    } else if (nameControl?.hasError('pattern')) {
      validationMessages.push(
        'Document name can contain alphabets, numbers and spaces only.'
      );
    } else if (nameControl?.invalid) {
      validationMessages.push('Document Name is invalid.');
    }

    if (this.fileError) {
      validationMessages.push(
        this.fileError.endsWith('.') ? this.fileError : `${this.fileError}.`
      );
    } else if (!this.selectedFile) {
      validationMessages.push('Please upload a document.');
    } else if (this.selectedFile.type !== 'application/pdf') {
      validationMessages.push('Please upload a valid PDF document.');
    }

    if (!this.signers.length) {
      validationMessages.push('Please add at least one recipient.');
    }

    this.signers.controls.forEach((recipient, index) => {
      const recipientNumber = index + 1;
      const recipientName = recipient.get('name');
      const recipientEmail = recipient.get('email');
      const recipientPhone = recipient.get('phone');
      const recipientEsignType = recipient.get('esignType');

      if (recipientName?.hasError('required')) {
        validationMessages.push(
          `Recipient ${recipientNumber} name is required.`
        );
      } else if (recipientName?.hasError('pattern')) {
        validationMessages.push(
          `Recipient ${recipientNumber} name can contain alphabets and spaces only.`
        );
      } else if (recipientName?.invalid) {
        validationMessages.push(`Recipient ${recipientNumber} name is invalid.`);
      }

      if (recipientEmail?.hasError('required')) {
        validationMessages.push(
          `Recipient ${recipientNumber} email is required.`
        );
      } else if (recipientEmail?.invalid) {
        validationMessages.push(
          `Recipient ${recipientNumber} email is invalid.`
        );
      }

      if (recipientPhone?.hasError('required')) {
        validationMessages.push(
          `Recipient ${recipientNumber} mobile number is required.`
        );
      } else if (recipientPhone?.invalid) {
        validationMessages.push(
          `Recipient ${recipientNumber} mobile number is invalid.`
        );
      }

      if (recipientEsignType?.hasError('required')) {
        validationMessages.push(
          `Recipient ${recipientNumber} E-sign Type is required.`
        );
      }

      const savedPosition = requireSignatures
        ? this.documentService.getSigners[index]?.signatureDetail
        : this.allSignersPositions[index]?.signatureData ??
        this.allSignersPositions[index]?.signatureDetail;

      if (!this.hasSignature(savedPosition)) {
        const displayName =
          String(recipientName?.value ?? '').trim() ||
          String(recipientEmail?.value ?? '').trim() ||
          `Signer ${recipientNumber}`;

        validationMessages.push(
          `Signature position is required for ${displayName}.`
        );
      }
    });

    const uniqueValidationMessages = [...new Set(validationMessages)];

    if (!uniqueValidationMessages.length) {
      return true;
    }

    this.loading = false;
    this.apiValidationTitle = 'Complete Required Details';
    this.apiValidationError = {
      message: '',
      errors: uniqueValidationMessages
    };

    setTimeout(() => {
      this.apiValidationMessage?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });

    return false;
  }

  private normalizeNameValues(): void {
    const documentName = this.documentForm.get('name');
    documentName?.setValue(
      String(documentName.value ?? '').replace(/\s+/g, ' ').trim(),
      { emitEvent: false }
    );

    this.signers.controls.forEach(recipient => {
      const recipientName = recipient.get('name');
      recipientName?.setValue(
        String(recipientName.value ?? '').replace(/\s+/g, ' ').trim(),
        { emitEvent: false }
      );
    });
  }

  private hasSignature(signatureDetail: any): boolean {
    const pages = Array.isArray(signatureDetail)
      ? signatureDetail
      : signatureDetail ? [signatureDetail] : [];

    return pages.some((page: any) =>
      Array.isArray(page?.coordinates)
        ? page.coordinates.length > 0
        : !!page?.coordinates
    );
  }

  private openSendConfirmation(onConfirm: () => void): void {
    this.confirmModalService.open(
      'Send Document',
      'Are you sure you want to send this document to the selected recipients?',
      () => {
        if (this.loading || this.submissionInProgress) {
          return;
        }

        this.submissionInProgress = true;
        onConfirm();
      },
      'Send Document',
      'Cancel'
    );
  }

  onStepClick(event: MouseEvent) {
    if (
      !this.documentForm.get('file')?.value ||
      !this.documentForm.get('name')?.value
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onBack() {
    this.updatedPdf = '';
  }


  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const maxSize = 10.1 * 1024 * 1024;
    if (file.size > maxSize) {
      this.fileError = 'File size must be less than 10MB';
      this.resetFile(event);
      return;
    }
    if (file?.type === 'application/pdf') {
      this.selectedFile = file;
      this.pdfPreviewUrl = URL.createObjectURL(file);
      this.fileError = '';
    } else {
      this.fileError = 'Only PDF files are allowed';
      this.resetFile(event);
    }
  }

  resetFile(event: any) {
    event.target.value = '';
    this.selectedFile = undefined;
    this.pdfPreviewUrl = '';
  }


  signDoc(index: any) {
    const recipient = this.documentService.getSigners[index]
    this.signData = {
      pdfUrl: this.updatedPdf || this.pdfPreviewUrl,
      name: recipient?.name,
      esignType: recipient?.esignType,
      signatureDetail: recipient?.signatureDetail,
      signerIndex: index,
      editable: true
    }
    this.isShowDoc = true;
  }

  close(event: any) {
    this.isShowDoc = false;
    this.currentStepIndex = event?.stepIndex ?? 0;

    if (event?.signatureData && event?.signerIndex !== undefined) {
      const currentSigners = this.documentService.getSigners;
      const updatedSigners = currentSigners.map((signer, index) => {
        if (index === event.signerIndex) {
          return {
            ...signer,
            signatureDetail: event.signatureData
          };
        }
        return signer;
      });

      this.documentService.setSigners = updatedSigners;

      this.toastService.showToaster(
        "Signature Saved",
        "The signature position has been saved successfully.",
        "success"
      );
      this.updatedPdf = event?.pdfUrl;
    }

    setTimeout(() => {
      if (!this.stepper) return;

      this.stepper.linear = false;
      this.stepper.selectedIndex = this.currentStepIndex;

      setTimeout(() => {
        this.stepper.linear = true;
      });
    });
  }

  selectedTemplate: any = {};
  isUsedTemplateDoc: boolean = false;
  allSignersPositions: any = [];

  getTemplates() {
    this.templateService
      .getAllTemplatesById(this.templateId)
      .subscribe(
        (res: any) => {
          this.selectedTemplate = res;
          this.allSignersPositions =
            res?.allSignersPositions;

          this.documentForm
            .get('name')
            ?.setValue(res?.name);

          const signersArray =
            this.documentForm.get('signers') as FormArray;

          signersArray.clear();

          this.allSignersPositions.map(() => {
            this.signers.push(
              this.createSigner()
            );
          });
        },

        (err) => {
          this.loading = false;
        }
      );
  }

  clearApiValidationMessage(): void {
    this.apiValidationError = null;
  }
  back() {
    this.router.navigate(['/customer/templates'])
  }


  dropSigner(event: CdkDragDrop<FormArray>) {
    if (event.previousIndex === event.currentIndex) return;

    const signersArray = this.signers as FormArray;
    const control = signersArray.at(event.previousIndex);

    signersArray.removeAt(event.previousIndex);
    signersArray.insert(event.currentIndex, control);
  }

  esignTypes = [
    { value: 1, label: 'Aadhar' },
    { value: 2, label: 'DSC' },
    { value: 3, label: 'Aadhar Biometric' }
  ];

  getEsignTypeVlaue(value: any) {
    const eSignType: any = this.esignTypes.find(type => type.value.toString() === value);
    return eSignType.label;
  }

  goToNextStep(stepper: any) {
    if (this.signers.invalid) {
      this.signers.markAllAsTouched();
      return;
    }
    this.documentService.updateSigners(this.signers.value);
    stepper.next();
  }

  noDuplicateDomainValidator(control: AbstractControl) {
    const email = control.value;
    if (!email) return null;

    if (email.toLowerCase().includes('.com.com')) {
      return { doubleCom: true };
    }

    return null;
  }

  allowDigitsOnly(event: KeyboardEvent) {
    const char = event.key;
    if (!/^[0-9]$/.test(char)) {
      event.preventDefault();
    }
  }

  allowDocumentNameCharacters(event: KeyboardEvent) {
    if (event.key.length === 1 && !/[A-Za-z0-9 ]/.test(event.key)) {
      event.preventDefault();
    }
  }

  allowRecipientNameCharacters(event: KeyboardEvent) {
    if (event.key.length === 1 && !/[A-Za-z ]/.test(event.key)) {
      event.preventDefault();
    }
  }

  filterPhoneInput(event: Event, control: AbstractControl) {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '');
    if (input.value !== filtered) {
      input.value = filtered;
      control.setValue(filtered, { emitEvent: false });
    }
  }

  filterDocumentNameInput(event: Event, control: AbstractControl) {
    const input = event.target as HTMLInputElement;

    const filtered = input.value
      .replace(/[^A-Za-z0-9\s]/g, '')
      .replace(/\s+/g, ' ');

    if (input.value !== filtered) {
      input.value = filtered;
      control.setValue(filtered, { emitEvent: false });
    }
  }

  filterRecipientNameInput(event: Event, control: AbstractControl) {
    const input = event.target as HTMLInputElement;

    const filtered = input.value
      .replace(/[^A-Za-z\s]/g, '')
      .replace(/\s+/g, ' ');

    if (input.value !== filtered) {
      input.value = filtered;
      control.setValue(filtered, { emitEvent: false });
    }
  }
}
